import { LitElement, html, PropertyValues } from 'lit';
import style from './kng-legal-acte-form.style';

function todayLocalISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoToSwiss(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const trimmed = String(isoDate).trim();
  const parts = trimmed.split('-');
  if (parts.length === 3) {
    return `${parts[2].padStart(2, '0')}.${parts[1].padStart(2, '0')}.${parts[0]}`;
  }
  return '';
}

function swissToIso(swissDate: string | null | undefined): string | null {
  if (!swissDate) return '';
  const match = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(String(swissDate).trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export class KngLegalActeForm extends LitElement {
  static properties = {
    refOwner: { type: String },
    refBuilding: { type: String },
    refTenant: { type: String },
    caseId: { type: String },
    actType: { type: String },
    actDate: { type: String },
    quantity: { type: Number },
    hours: { type: Number },
    minutes: { type: Number },
    notes: { type: String },
    status: { type: String },
    rates: { type: Array },
    actTypeOptions: { type: Array },
    rateId: { type: String },
    isFetchingRates: { type: Boolean },
    includeInReport: { type: Boolean },
    actSearchQuery: { type: String },
    amountHT: { type: Number },
    finalAmountHT: { type: String },
    overrideReason: { type: String },
    selectedRateAmount: { type: Number },
    selectedRateUnit: { type: String },
    editingAct: { type: Object }
  };

  static styles = style;

  refOwner: string = '';
  refBuilding: string = '';
  refTenant: string = '';
  caseId: string = '';
  actType: string = '';
  actDate: string = todayLocalISO();
  quantity: number = 1;
  hours: number = 1;
  minutes: number = 0;
  notes: string = '';
  status: string = 'validated';
  rates: any[] = [];
  actTypeOptions: any[] = [];
  rateId: string = '';
  isFetchingRates: boolean = false;
  includeInReport: boolean = true;
  actSearchQuery: string = '';
  amountHT: number | null = null;
  finalAmountHT: string | null = null;
  overrideReason: string = '';
  selectedRateAmount: number = 0;
  selectedRateUnit: string = 'forfait';
  editingAct: any = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('editingAct')) {
      if (this.editingAct) {
        this.#loadEditingAct(this.editingAct);
      }
    } else if (changedProperties.has('refTenant') && this.refTenant && !this.editingAct) {
      this.#loadDraft();
    }
    if (changedProperties.has('refOwner') || changedProperties.has('actDate')) {
      this.#fetchAllRates();
    }
  }

  #getDraftKey(): string {
    return `prsa-acte-draft-${this.refTenant}`;
  }

  #saveDraft(): void {
    if (!this.refTenant) return;
    const draft = {
      actType: this.actType,
      actDate: this.actDate,
      quantity: this.quantity,
      hours: this.hours,
      minutes: this.minutes,
      notes: this.notes,
      status: this.status,
      rateId: this.rateId,
      amountHT: this.amountHT,
      finalAmountHT: this.finalAmountHT,
      overrideReason: this.overrideReason,
      includeInReport: this.includeInReport
    };
    sessionStorage.setItem(this.#getDraftKey(), JSON.stringify(draft));
  }

  #loadEditingAct(act: any): void {
    if (!act) return;
    this.actType = act.actType || '';
    this.actDate = act.actDate ? act.actDate.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const qty = Number(act.quantity) || 1;
    this.quantity = qty;
    this.hours = Math.floor(qty);
    this.minutes = Math.round((qty - this.hours) * 60);
    this.notes = act.notes || '';
    this.status = act.status || 'validated';
    this.rateId = act.rateId || '';
    this.amountHT = act.amountHT !== undefined ? act.amountHT : null;
    this.finalAmountHT = act.finalAmountHT !== undefined ? String(act.finalAmountHT) : null;
    this.overrideReason = act.overrideReason || '';
    this.includeInReport = act.includeInReport !== 0;
    this.selectedRateAmount = act.rateAmount !== undefined ? Number(act.rateAmount) : 0;
    this.selectedRateUnit = act.rateUnit || 'forfait';
  }

  #loadDraft(): void {
    if (!this.refTenant) return;
    
    const draftJson = sessionStorage.getItem(this.#getDraftKey());
    if (draftJson) {
      try {
        const draft = JSON.parse(draftJson);
        this.actType = draft.actType || '';
        this.actDate = todayLocalISO();
        this.quantity = draft.quantity || 1;
        this.hours = draft.hours !== undefined ? draft.hours : Math.floor(this.quantity);
        this.minutes = draft.minutes !== undefined ? draft.minutes : Math.round((this.quantity - this.hours) * 60);
        this.notes = draft.notes || '';
        this.status = draft.status || 'validated';
        this.rateId = draft.rateId || '';
        this.amountHT = draft.amountHT !== undefined ? draft.amountHT : null;
        this.finalAmountHT = draft.finalAmountHT !== undefined ? draft.finalAmountHT : null;
        this.overrideReason = draft.overrideReason || '';
        if (draft.includeInReport !== undefined) this.includeInReport = draft.includeInReport;
      } catch (e) {
        console.error('Failed to parse draft', e);
      }
    } else {
      this.actDate = todayLocalISO();
      this.actType = '';
      this.quantity = 1;
      this.hours = 1;
      this.minutes = 0;
      this.notes = '';
      this.status = 'validated';
      this.rateId = '';
      this.amountHT = null;
      this.finalAmountHT = null;
      this.overrideReason = '';
      this.includeInReport = true;
    }
  }

  get currentRateAmount(): string {
    return this.selectedRateAmount.toFixed(2);
  }

  get currentRateUnit(): string {
    return this.selectedRateUnit || 'forfait';
  }

  get filteredActTypeOptions(): any[] {
    if (!this.actSearchQuery) return this.actTypeOptions;
    const query = this.actSearchQuery.toLowerCase();
    return this.actTypeOptions.filter(opt => 
      opt.actType.toLowerCase().includes(query) || 
      (opt.refOwner && opt.refOwner.toLowerCase().includes(query))
    );
  }

  render() {
    const isEditing = Boolean(this.editingAct);
    return html`
      <div class="panel-card" style="padding: var(--sl-spacing-medium); background: var(--sl-color-neutral-0); border: 1px solid var(--sl-color-neutral-200); border-radius: var(--sl-border-radius-large); margin: 0;">
        <div style="margin-bottom: var(--sl-spacing-small); border-bottom: 1px solid var(--sl-color-neutral-100); padding-bottom: var(--sl-spacing-x-small); display: flex; justify-content: space-between; align-items: center;">
          <strong style="text-transform: uppercase; color: var(--sl-color-brand-950); font-size: var(--sl-font-size-small);">
            ${isEditing ? "Modifier l'acte" : "Ajouter un acte"}
          </strong>
          ${isEditing ? html`
            <sl-button size="small" variant="default" @click=${this.#handleCancel}>
              Annuler
            </sl-button>
          ` : ''}
        </div>
        <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-medium);">
          
          <!-- Row 1: Type, Date, Rate display -->
          <div style="display: flex; gap: var(--sl-spacing-medium); align-items: center; flex-wrap: wrap;">
            <div style="flex: 2; min-width: 200px;">
              <sl-dropdown placement="bottom-start" class="act-type-dropdown" hoist>
                <sl-button slot="trigger" size="small" style="width: 100%; text-align: left; justify-content: space-between;">
                  ${this.actType ? html`<span .textContent=${this.actType}></span> ▾` : 'Élément facturé ▾ 🔍'}
                </sl-button>
                <div class="combobox-panel">
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 var(--sl-spacing-small);">
                    <sl-input size="small" placeholder="Rechercher..." clearable @sl-input=${this.#handleSearchChange} value=${this.actSearchQuery} style="flex: 1;"></sl-input>
                    <a href="/tarifs" target="_blank" style="font-size: 0.8rem; color: var(--sl-color-primary-600); text-decoration: none; margin-left: var(--sl-spacing-small); white-space: nowrap;">(Tarif manquant ?)</a>
                  </div>
                  <sl-menu class="combobox-menu">
                    ${this.actTypeOptions.length === 0 ? html`<sl-menu-item disabled>Chargement...</sl-menu-item>` : ''}
                    ${this.filteredActTypeOptions.map(opt => html`
                      <sl-menu-item value="${opt.rateId}" @click=${() => this.#selectRate(opt.rateId)} style="font-family: monospace;">
                        <div style="display: grid; grid-template-columns: 1fr auto 90px; gap: 8px; width: 100%; align-items: center;">
                          <span style="white-space: normal; line-height: 1.2;">${opt.actType}</span>
                          <span>${opt.refOwner ? html`<sl-badge variant="primary" pill>${opt.refOwner}</sl-badge>` : ''}</span>
                          <span style="text-align: right;">CHF ${opt.amount.toFixed(2)} / ${opt.rateUnit || 'unité'}</span>
                        </div>
                      </sl-menu-item>
                    `)}
                    ${this.filteredActTypeOptions.length === 0 && this.actTypeOptions.length > 0 ? html`<sl-menu-item disabled>Aucun résultat</sl-menu-item>` : ''}
                  </sl-menu>
                </div>
              </sl-dropdown>
            </div>
            <div style="flex: 1; min-width: 150px; position: relative;">
              <sl-input
                size="small"
                placeholder="jj.mm.aaaa"
                .value=${isoToSwiss(this.actDate)}
                @sl-input=${this.#handleSwissDateInput}
                @sl-change=${this.#handleSwissDateChange}>
                <sl-icon
                  slot="suffix"
                  name="calendar"
                  style="cursor: pointer; color: var(--sl-color-neutral-500);"
                  title="Sélectionner une date"
                  @click=${(e: Event) => {
                    e.stopPropagation();
                    const picker = this.renderRoot?.querySelector('#act-date-native-picker') as any;
                    if (picker) {
                      try {
                        if (typeof picker.showPicker === 'function') picker.showPicker();
                        else picker.click();
                      } catch {
                        picker.click();
                      }
                    }
                  }}>
                </sl-icon>
              </sl-input>
              <input
                id="act-date-native-picker"
                type="date"
                tabindex="-1"
                aria-hidden="true"
                style="position: absolute; opacity: 0; pointer-events: none; width: 0; height: 0; border: 0; bottom: 0; right: 0;"
                .value=${this.actDate}
                @change=${(e: any) => {
                  this.actDate = e.target.value || '';
                  this.#saveDraft();
                  this.requestUpdate();
                }}
              />
            </div>
            <div style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">
              Tarif CHF <span .textContent=${this.currentRateAmount}></span> (<span .textContent=${this.currentRateUnit}></span>)
            </div>
          </div>

          <!-- Row 2: Qty / h-min, Montant figé -->
          <div style="display: flex; gap: var(--sl-spacing-medium); align-items: center; flex-wrap: wrap; justify-content: space-between;">
            ${this.selectedRateUnit.toLowerCase() === 'heure' ? html`
              <div style="display: flex; align-items: center; gap: var(--sl-spacing-x-small);">
                <span style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">Durée :</span>
                <sl-input size="small" type="number" min="0" step="1" .value=${String(this.hours)} @sl-input=${this.#handleHoursChange} style="width: 60px;"></sl-input>
                <span style="font-size: 0.85rem; color: var(--sl-color-neutral-500);">h</span>
                <sl-input size="small" type="number" min="0" max="59" step="5" .value=${String(this.minutes)} @sl-input=${this.#handleMinutesChange} style="width: 60px;"></sl-input>
                <span style="font-size: 0.85rem; color: var(--sl-color-neutral-500);">min</span>
                <span style="font-size: 0.8rem; color: var(--sl-color-neutral-400); margin-left: 4px;">(${this.quantity.toFixed(2)} h)</span>
              </div>
            ` : (this.selectedRateUnit.toLowerCase() === 'forfait' ? html`
              <div style="display: flex; align-items: center; gap: var(--sl-spacing-x-small);">
                <span style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">Qté :</span>
                <sl-input size="small" disabled value="1" style="width: 60px;"></sl-input>
                <span style="font-size: 0.85rem; color: var(--sl-color-neutral-500); white-space: nowrap;">(forfait)</span>
              </div>
            ` : html`
              <div style="display: flex; align-items: center; gap: var(--sl-spacing-x-small);">
                <span style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">Qté :</span>
                <sl-input size="small" type="number" min="1" step="1" .value=${String(this.quantity)} @sl-input=${this.#handleQuantityChange} style="width: 70px;"></sl-input>
                <span style="font-size: 0.85rem; color: var(--sl-color-neutral-500); white-space: nowrap;">× <span .textContent=${this.currentRateUnit}></span></span>
              </div>
            `)}
            
            <div style="display: flex; align-items: center; gap: var(--sl-spacing-small);">
              <span style="font-size: 0.9rem; color: var(--sl-color-neutral-600); white-space: nowrap;">HT CHF</span>
              <sl-input size="small" readonly .value=${this.#calculateMontantHT()} style="width: 105px; font-weight: bold;" title="Montant figé par le tarif"></sl-input>
              <sl-badge variant="neutral" pill style="font-size: 0.75rem;">figé</sl-badge>
            </div>
          </div>

          <!-- Row 3: Note obligatoire -->
          <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-x-small);">
            <sl-input size="small" placeholder="Note (obligatoire) *" .value=${this.notes} @sl-input=${this.#handleNotesChange} required></sl-input>
            ${!this.notes.trim() && this.actType ? html`
              <span style="font-size: 0.75rem; color: var(--sl-color-danger-600);">* Note obligatoire</span>
            ` : ''}
          </div>
          
          <!-- Row 4: Statut, Coche inclure, Actions -->
          <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--sl-spacing-small); margin-top: var(--sl-spacing-small);">
            <div style="display: flex; align-items: center; gap: var(--sl-spacing-medium);">
              <div style="display: flex; align-items: center; gap: var(--sl-spacing-x-small);">
                <span style="font-size: 0.85rem; color: var(--sl-color-neutral-600); white-space: nowrap;">Statut :</span>
                <sl-select size="small" .value=${this.status} @sl-change=${this.#handleStatusChange} style="width: 130px;">
                  <sl-option value="validated">Validé</sl-option>
                  <sl-option value="draft">Brouillon</sl-option>
                </sl-select>
              </div>
              <sl-checkbox size="small" ?checked=${this.includeInReport} @sl-change=${this.#handleIncludeChange}>Inclure au rapport</sl-checkbox>
            </div>
            <div style="display: flex; gap: var(--sl-spacing-small);">
              ${isEditing ? html`
                <sl-button size="small" variant="default" @click=${this.#handleCancel}>Annuler</sl-button>
              ` : ''}
              <sl-button size="small" variant=${this.status === 'draft' ? 'default' : 'primary'} @click=${this.#handleSubmit} ?disabled=${this.#isSubmitDisabled()}>
                ${isEditing ? (this.status === 'draft' ? "Enregistrer brouillon" : "Enregistrer") : (this.status === 'draft' ? "Créer brouillon" : "Ajouter l'acte")}
              </sl-button>
            </div>
          </div>

        </div>
      </div>
    `;
  }

  #handleActTypeChange(event: any) {
    this.actType = event.target.value || '';
    this.#saveDraft();
  }

  #handleSwissDateInput(event: any) {
    const raw = event.target.value;
    const iso = swissToIso(raw);
    if (iso) {
      this.actDate = iso;
      this.#saveDraft();
    }
  }

  #handleSwissDateChange(event: any) {
    const raw = event.target.value;
    if (!raw || !raw.trim()) {
      this.actDate = '';
    } else {
      const iso = swissToIso(raw);
      if (iso) {
        this.actDate = iso;
      } else {
        event.target.value = isoToSwiss(this.actDate);
      }
    }
    this.#saveDraft();
    this.requestUpdate();
  }

  #handleActDateChange(event: any) {
    this.actDate = event.target.value || '';
    this.#saveDraft();
  }

  #handleSearchChange(event: any) {
    this.actSearchQuery = event.target.value || '';
  }

  #selectRate(rateId: string) {
    this.rateId = rateId;
    const selectedOpt = this.actTypeOptions.find(o => o.rateId === this.rateId);
    if (selectedOpt) {
      this.actType = selectedOpt.actType;
      this.selectedRateAmount = Number(selectedOpt.amount) || 0;
      this.selectedRateUnit = selectedOpt.rateUnit || 'forfait';
      if (this.selectedRateUnit.toLowerCase() === 'forfait') {
        this.quantity = 1;
        this.hours = 1;
        this.minutes = 0;
      } else if (this.selectedRateUnit.toLowerCase() === 'heure') {
        if (!this.hours && !this.minutes) {
          this.hours = 1;
          this.minutes = 0;
          this.quantity = 1;
        }
      }
      this.amountHT = this.selectedRateAmount;
      this.finalAmountHT = (this.selectedRateAmount * this.quantity).toFixed(2);
      this.overrideReason = '';
    }
    this.#saveDraft();
    this.requestUpdate();
  }

  async #fetchAllRates() {
    if (!this.refOwner || !this.actDate) {
      this.actTypeOptions = [];
      return;
    }
    this.isFetchingRates = true;
    try {
      const response = await fetch(`/api/legal/rates?refOwner=${encodeURIComponent(this.refOwner)}&actDate=${encodeURIComponent(this.actDate)}`);
      const allRates = response.ok ? await response.json() : [];
      
      // Sort to prefer owner specific rate, then alphabetically by actType
      allRates.sort((a: any, b: any) => {
        if (a.refOwner && !b.refOwner) return -1;
        if (!a.refOwner && b.refOwner) return 1;
        return a.actType.localeCompare(b.actType);
      });

      this.actTypeOptions = allRates.map((r: any) => ({
        actType: r.actType,
        rateId: r.id,
        amount: r.amount,
        refOwner: r.refOwner,
        rateUnit: r.rateUnit || 'unité',
        active: r.active !== false
      }));
      
      // Keep rateId mapped if actType already set (e.g. initial load), otherwise wait for user input
      if (this.actType && this.actTypeOptions.length > 0) {
         const selectedOpt = this.actTypeOptions.find(o => o.actType === this.actType);
         if (selectedOpt) {
           this.rateId = selectedOpt.rateId;
           this.selectedRateAmount = Number(selectedOpt.amount) || 0;
           this.selectedRateUnit = selectedOpt.rateUnit || 'unité';
         } else {
           this.actType = '';
           this.rateId = '';
           this.selectedRateAmount = 0;
           this.selectedRateUnit = 'forfait';
         }
      }

      this.requestUpdate();
    } catch (e) {
      console.error('Error fetching all rates', e);
    } finally {
      this.isFetchingRates = false;
    }
  }

  #calculateMontantHT(): string {
    const selectedOpt = this.actTypeOptions.find(o => o.rateId === this.rateId);
    const unitAmount = selectedOpt ? Number(selectedOpt.amount) : this.selectedRateAmount;
    return (unitAmount * this.quantity).toFixed(2);
  }

  #handleQuantityChange(event: any) {
    const nextQty = parseFloat(event.target.value);
    this.quantity = Number.isFinite(nextQty) && nextQty > 0 ? nextQty : 1;
    this.#saveDraft();
    this.requestUpdate();
  }

  #handleHoursChange(event: any) {
    const h = Math.max(0, parseInt(event.target.value, 10) || 0);
    this.hours = h;
    const total = h + (this.minutes || 0) / 60;
    this.quantity = parseFloat(total.toFixed(2));
    this.#saveDraft();
    this.requestUpdate();
  }

  #handleMinutesChange(event: any) {
    const m = Math.max(0, Math.min(59, parseInt(event.target.value, 10) || 0));
    this.minutes = m;
    const total = (this.hours || 0) + m / 60;
    this.quantity = parseFloat(total.toFixed(2));
    this.#saveDraft();
    this.requestUpdate();
  }

  #handleStatusChange(event: any) {
    this.status = event.target.value || 'validated';
    this.#saveDraft();
    this.requestUpdate();
  }

  #handleNotesChange(event: any) {
    this.notes = event.target.value || '';
    this.#saveDraft();
    this.requestUpdate();
  }

  #handleFinalAmountChange() {
    this.requestUpdate();
  }

  #handleOverrideReasonChange(event: any) {
    this.overrideReason = event.target.value || '';
    this.#saveDraft();
  }

  #isAmountOverridden(): boolean {
    return false;
  }

  #isSubmitDisabled(): boolean {
    if (!this.actType) return true;
    if (!this.notes || !this.notes.trim()) return true;
    if (this.quantity <= 0) return true;
    return false;
  }

  #handleIncludeChange(event: any) {
    this.includeInReport = event.target.checked;
    this.#saveDraft();
  }

  #handleCancel() {
    this.editingAct = null;
    this.notes = '';
    this.quantity = 1;
    this.hours = 1;
    this.minutes = 0;
    this.status = 'validated';
    this.amountHT = null;
    this.finalAmountHT = null;
    this.overrideReason = '';
    this.actType = '';
    this.rateId = '';
    sessionStorage.removeItem(this.#getDraftKey());
    this.dispatchEvent(new CustomEvent('kng-acte-cancel', { bubbles: true, composed: true }));
    this.requestUpdate();
  }

  #handleSubmit() {
    if (this.#isSubmitDisabled()) return;
    const calculatedTotal = parseFloat(this.#calculateMontantHT());
    const qty = Math.max(0.01, this.quantity || 1);

    const detailPayload = {
      id: this.editingAct?.id,
      caseId: this.caseId,
      refOwner: this.refOwner,
      refBuilding: this.refBuilding,
      refTenant: this.refTenant,
      actType: this.actType,
      actDate: this.actDate,
      rateId: this.rateId,
      quantity: qty,
      amountHT: this.selectedRateAmount,
      finalAmountHT: calculatedTotal,
      status: this.status || 'validated',
      notes: this.notes.trim(),
      includeInReport: this.includeInReport,
      updatedAt: this.editingAct?.updatedAt
    };

    this.dispatchEvent(new CustomEvent('kng-acte-submit', {
      detail: detailPayload,
      bubbles: true,
      composed: true,
    }));
    
    // Save last submitted date
    sessionStorage.setItem('prsa-last-act-date', this.actDate);
    // Clear draft storage
    sessionStorage.removeItem(this.#getDraftKey());
    
    // Reset fields for the next entry (but keep the date)
    this.editingAct = null;
    this.actType = '';
    this.rateId = '';
    this.quantity = 1;
    this.hours = 1;
    this.minutes = 0;
    this.notes = '';
    this.amountHT = null;
    this.finalAmountHT = null;
    this.overrideReason = '';
    this.status = 'validated';
    this.requestUpdate();
  }
}
